/**
 * 問診票管理ページ
 * 上: 全問診票のテーブル（目/編集/削除）
 * 下: 健康心理条件を選択して新規作成・編集する編集パネル
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getConditions,
  getCondition,
  getConditionQuestionnaires,
  getConditionQuestionnaire,
  generateConditionQuestionnaire,
  updateConditionQuestionnaire,
  updateConditionQuestionnaireReviewStatus,
  getAllConditionQuestionnaires,
  deleteConditionQuestionnaire,
} from '../../lib/admin-api';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Loader2, FileText, Plus, Eye, Edit, Trash2 } from 'lucide-react';
import { QuestionnaireEditView } from '../../components/admin/QuestionnaireEditView';
import { ConditionCategoryTable, type CategoryTableData } from '../../components/admin/ConditionCategoryTable';
import { QuestionnaireDocument, type QuestionnaireJsonData } from '../../components/QuestionnaireDocument';
import { useToast } from '../../hooks/use-toast';
import { getLegacyCategoryTable } from '../../lib/conditionTableNormalizer';

function formatDate(s: string | Date | null | undefined): string {
  if (!s) return '-';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '-' : d.toLocaleString('ja-JP');
}

function reviewStatusLabel(status: string): string {
  const map: Record<string, string> = {
    draft: '下書き',
    in_review: 'レビュー中',
    approved: '承認済',
  };
  return map[status] ?? status;
}

function ReviewStatusPill({ status }: { status: string }) {
  const tone: Record<string, { bg: string; color: string }> = {
    approved: { bg: '#DCFCE7', color: '#15803D' },
    in_review: { bg: '#FEF3C7', color: '#B45309' },
    draft: { bg: '#F4F4F5', color: '#52525B' },
  };
  const t = tone[status] ?? tone.draft;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 96,
        padding: '7px 10px',
        borderRadius: 8,
        background: t.bg,
        color: t.color,
        border: `1px solid ${t.color}`,
        fontSize: 13,
        fontWeight: 800,
        whiteSpace: 'nowrap',
      }}
    >
      {reviewStatusLabel(status)}
    </span>
  );
}

type AllQuestionnaire = {
  id: number;
  condition_id: number;
  gender?: string;
  age_range?: string;
  review_status?: string;
  created?: string;
  updated?: string;
  questionnaire_data?: {
    questionnaire_text?: string;
    questionnaire_json?: QuestionnaireJsonData;
  };
};

export default function QuestionnairesList() {
  const initialConditionId = typeof window !== 'undefined'
    ? (new URLSearchParams(window.location.search).get('conditionId') ?? '')
    : '';
  const [conditionId, setConditionId] = useState<string>(initialConditionId);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<number | null>(null);
  useEffect(() => {
    if (initialConditionId) {
      setTimeout(() => {
        document.getElementById('questionnaire-edit-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [initialConditionId]);
  const [viewQuestionnaire, setViewQuestionnaire] = useState<AllQuestionnaire | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AllQuestionnaire | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: allData, isLoading: isAllLoading } = useQuery({
    queryKey: ['admin', 'all-condition-questionnaires'],
    queryFn: () => getAllConditionQuestionnaires(),
  });
  const allQuestionnaires: AllQuestionnaire[] = allData?.questionnaires ?? [];

  const { data: conditionsData } = useQuery({
    queryKey: ['admin', 'conditions'],
    queryFn: () => getConditions(),
  });
  const conditions = conditionsData?.conditions ?? [];
  const selectedId = conditionId ? parseInt(conditionId, 10) : null;
  const selectedConditionSummary = conditions.find((c: { id: number }) => c.id === selectedId) ?? null;
  const { data: questionnairesData, isLoading } = useQuery({
    queryKey: ['admin', 'condition-questionnaires', selectedId],
    queryFn: () => getConditionQuestionnaires(selectedId!),
    enabled: !!selectedId,
  });
  const questionnaires = questionnairesData?.questionnaires ?? [];

  const { data: selectedConditionData, isLoading: isConditionLoading } = useQuery({
    queryKey: ['admin', 'condition', selectedId],
    queryFn: () => getCondition(selectedId!),
    enabled: !!selectedId,
  });
  const selectedCondition = selectedConditionData?.condition ?? selectedConditionSummary ?? null;
  const categoryTable = getLegacyCategoryTable(selectedCondition?.condition_data) as CategoryTableData | null;

  const { data: selectedQuestionnaireData, isLoading: isSelectedQuestionnaireLoading } = useQuery({
    queryKey: ['admin', 'condition-questionnaire', selectedQuestionnaireId],
    queryFn: () => getConditionQuestionnaire(selectedQuestionnaireId!),
    enabled: !!selectedQuestionnaireId,
  });
  const selectedQuestionnaire = selectedQuestionnaireData?.questionnaire ?? null;

  const generateMutation = useMutation({
    mutationFn: generateConditionQuestionnaire,
    onSuccess: (data: { questionnaire?: { id: number } }) => {
      const id = data?.questionnaire?.id;
      if (id) {
        setSelectedQuestionnaireId(id);
        queryClient.invalidateQueries({ queryKey: ['admin', 'condition-questionnaires', selectedId] });
        queryClient.invalidateQueries({ queryKey: ['admin', 'all-condition-questionnaires'] });
        toast({ title: '問診票を生成しました' });
      }
    },
    onError: (error: Error) => {
      toast({ title: '問診票生成エラー', description: error.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteConditionQuestionnaire(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'all-condition-questionnaires'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'condition-questionnaires', selectedId] });
      if (selectedQuestionnaireId && deleteTarget?.id === selectedQuestionnaireId) {
        setSelectedQuestionnaireId(null);
      }
      setDeleteTarget(null);
      toast({ title: '問診票を削除しました' });
    },
    onError: (error: Error) => {
      toast({ title: '削除エラー', description: error.message, variant: 'destructive' });
    },
  });

  const handleCreateNew = () => {
    if (!selectedId) return;
    generateMutation.mutate(selectedId);
  };

  const handleUpdate = async (id: number, data: object) => {
    await updateConditionQuestionnaire(id, data);
    queryClient.invalidateQueries({ queryKey: ['admin', 'condition-questionnaire', id] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'condition-questionnaires', selectedId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'all-condition-questionnaires'] });
    toast({ title: '問診票を保存しました' });
  };

  const handleUpdateReviewStatus = async (id: number, status: 'approved' | 'draft' | 'in_review') => {
    await updateConditionQuestionnaireReviewStatus(id, status);
    queryClient.invalidateQueries({ queryKey: ['admin', 'condition-questionnaire', id] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'condition-questionnaires', selectedId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'all-condition-questionnaires'] });
    toast({ title: status === 'approved' ? 'レビュー完了にしました' : 'レビュー中にしました' });
  };

  const handleEditFromList = (q: AllQuestionnaire) => {
    setConditionId(String(q.condition_id));
    setSelectedQuestionnaireId(q.id);
    setTimeout(() => {
      document.getElementById('questionnaire-edit-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleBackToList = () => setSelectedQuestionnaireId(null);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
              <FileText className="w-8 h-8" />
              問診票管理
            </h1>
            <p className="text-base text-gray-500 mt-2">問診票を管理します</p>
          </div>
        </div>

        {/* 上: 全問診票のテーブル */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-4">問診票一覧</div>
          {isAllLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : allQuestionnaires.length === 0 ? (
            <p className="py-12 text-center text-base text-gray-500">
              問診票がまだ登録されていません。下の「健康心理条件から編集」から新規作成してください。
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-b border-gray-200 hover:bg-transparent">
                  <TableHead className="text-base font-bold text-gray-700 w-20 text-center">ID</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">健康心理条件ID</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">性別/年代</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">レビュー状態</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">作成日時</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 text-center">更新日時</TableHead>
                  <TableHead className="text-base font-bold text-gray-700 w-40 text-center">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allQuestionnaires.map((q) => (
                  <TableRow key={q.id} className="border-b border-gray-100 hover:bg-gray-50/50">
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums text-center">{q.id}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums text-center">{q.condition_id}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 text-center">{q.gender ?? '-'} / {q.age_range ?? '-'}</TableCell>
                    <TableCell className="py-4 text-center">
                      <ReviewStatusPill status={q.review_status ?? 'draft'} />
                    </TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums text-center">{formatDate(q.created)}</TableCell>
                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums text-center">{formatDate(q.updated)}</TableCell>
                    <TableCell className="py-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          type="button"
                          onClick={() => setViewQuestionnaire(q)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 text-blue-600 hover:bg-blue-50"
                          aria-label="プレビュー"
                          title="プレビュー"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEditFromList(q)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                          aria-label="編集"
                          title="編集"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(q)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                          aria-label="削除"
                          title="削除"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* 下: 健康心理条件から編集・新規作成 */}
        <div id="questionnaire-edit-section" className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="text-2xl font-bold text-gray-900 mb-2">健康心理条件から編集</div>
          <p className="text-base text-gray-500 mb-4">
            健康心理条件を選択して問診票の編集・新規作成ができます。
          </p>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="text-base font-bold text-gray-700 whitespace-nowrap">健康心理条件:</span>
              <Select value={conditionId} onValueChange={(v) => { setConditionId(v); setSelectedQuestionnaireId(null); }}>
                <SelectTrigger className="h-11 rounded-xl border-gray-200 w-80">
                  <SelectValue placeholder="条件を選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {conditions.map((c: { id: number; gender?: string; age_range?: string }) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      ID: {c.id} ({c.gender ?? '-'}/{c.age_range ?? '-'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {conditions.length === 0 && (
              <p className="text-base text-amber-600">
                健康心理条件が登録されていません。先に「健康心理条件管理」で条件を登録してください。
              </p>
            )}
            {conditions.length > 0 && !selectedId && (
              <p className="text-base text-gray-500">
                上記から健康心理条件を選択してください。
              </p>
            )}

            {selectedId && (
              <div className="flex flex-col gap-6">
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="text-2xl font-bold text-gray-900 mb-2">健康心理条件テーブル</div>
                  <p className="text-base text-gray-500 mb-2">
                    条件を確認しながら、下の一覧で問診票をレビュー・編集できます。
                  </p>
                  <div className="text-base text-gray-500 mb-4">
                    ID: {selectedCondition?.id ?? '-'} / {selectedCondition?.gender ?? '-'} / {selectedCondition?.age_range ?? '-'}
                  </div>
                  {isConditionLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : categoryTable ? (
                    <ConditionCategoryTable
                      categoryTable={categoryTable}
                      maxHeightClassName="max-h-[min(480px,55vh)]"
                    />
                  ) : (
                    <p className="text-base text-gray-500">この健康心理条件にはテーブルデータがありません。</p>
                  )}
                </div>

                <div className="min-w-0">
                  {selectedQuestionnaireId ? (
                    isSelectedQuestionnaireLoading ? (
                      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="flex items-center justify-center py-16">
                          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        </div>
                      </div>
                    ) : selectedQuestionnaire ? (
                      <QuestionnaireEditView
                        questionnaire={selectedQuestionnaire}
                        onBack={handleBackToList}
                        onUpdate={handleUpdate}
                        onUpdateReviewStatus={handleUpdateReviewStatus}
                      />
                    ) : (
                      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="py-12 text-center text-base text-gray-500">
                          問診票の取得に失敗しました。
                        </div>
                      </div>
                    )
                  ) : (
                    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                      <div className="text-2xl font-bold text-gray-900 mb-4">この条件の問診票</div>
                      <div className="space-y-4">
                        {isLoading ? (
                          <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                          </div>
                        ) : questionnaires.length === 0 ? (
                          <div className="py-12 text-center space-y-4">
                            <p className="text-base text-gray-500">問診票が未設定です。問診票を生成してください。</p>
                            <Button onClick={handleCreateNew} disabled={generateMutation.isPending} className="bg-gray-900 hover:bg-gray-800 text-white rounded-lg h-11 px-5 font-bold shadow-sm">
                              {generateMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              ) : (
                                <Plus className="w-4 h-4 mr-2" />
                              )}
                              新規作成
                            </Button>
                          </div>
                        ) : (
                          <>
                            <Table>
                              <TableHeader>
                                <TableRow className="border-b border-gray-200 hover:bg-transparent">
                                  <TableHead className="text-base font-bold text-gray-700 w-20">ID</TableHead>
                                  <TableHead className="text-base font-bold text-gray-700">レビュー状態</TableHead>
                                  <TableHead className="text-base font-bold text-gray-700">作成日時</TableHead>
                                  <TableHead className="text-base font-bold text-gray-700">更新日時</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {questionnaires.map((q: { id: number; review_status?: string; created?: string; updated?: string }) => (
                                  <TableRow
                                    key={q.id}
                                    className="border-b border-gray-100 cursor-pointer hover:bg-gray-50/50"
                                    onClick={() => setSelectedQuestionnaireId(q.id)}
                                  >
                                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{q.id}</TableCell>
                                    <TableCell className="py-4">
                                      <ReviewStatusPill status={q.review_status ?? 'draft'} />
                                    </TableCell>
                                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{formatDate(q.created)}</TableCell>
                                    <TableCell className="py-4 text-base font-medium text-gray-900 tabular-nums">{formatDate(q.updated)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                            <div className="border-t pt-4">
                              <Button variant="outline" size="sm" onClick={handleCreateNew} disabled={generateMutation.isPending} className="h-9 rounded-lg border-gray-200">
                                {generateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                                新規作成
                              </Button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* プレビューダイアログ（シナリオ選択後の画面と同じ QuestionnaireDocument で描画、氏名なし） */}
      <Dialog open={viewQuestionnaire !== null} onOpenChange={(open) => !open && setViewQuestionnaire(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>問診票プレビュー (ID: {viewQuestionnaire?.id})</DialogTitle>
          </DialogHeader>
          {viewQuestionnaire?.questionnaire_data?.questionnaire_json ? (
            <QuestionnaireDocument
              json={viewQuestionnaire.questionnaire_data.questionnaire_json}
              meta={{ gender: viewQuestionnaire.gender }}
            />
          ) : viewQuestionnaire?.questionnaire_data?.questionnaire_text ? (
            <pre className="text-base bg-gray-50 p-4 rounded-lg whitespace-pre-wrap border">
              {viewQuestionnaire.questionnaire_data.questionnaire_text}
            </pre>
          ) : (
            <p className="text-base text-gray-500">問診票が未設定です。問診票を生成してください。</p>
          )}
        </DialogContent>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <AlertDialog open={deleteTarget !== null} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>問診票を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              問診票ID {deleteTarget?.id} を削除します。この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            >
              削除
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
